import { useTranslate } from "@tolgee/react";
import { ChevronDown } from "lucide-react";
import { Button } from "#/components/ui/button";
import {
	Dialog,
	DialogCloseButton,
	DialogContent,
	DialogDescription,
	DialogTitle,
	DialogTrigger,
} from "#/components/ui/dialog.tsx";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "#/components/ui/tooltip.tsx";
import { getMarketDescription } from "../../lib/market-descriptions.ts";
import {
	getDefaultPerpetualDisplayName,
	getLocalizedMarketName,
	getMarketLogoAlt,
} from "../../lib/market-display.ts";
import { PerpMarketSearchDialog } from "./perp-market-search-dialog.tsx";
import type { useCoinLogo } from "./use-coin-logo.ts";

type MarketInfoHeaderProps = {
	currentCoin: string;
	dex?: string;
	logo: ReturnType<typeof useCoinLogo>;
	symbolLabel: string;
	symbolBadgeLabel: string;
};

export function MarketInfoHeader({
	currentCoin,
	dex,
	logo,
	symbolLabel,
	symbolBadgeLabel,
}: MarketInfoHeaderProps) {
	const { t } = useTranslate();

	const localizedSubtitle = getLocalizedMarketName({
		coin: currentCoin,
		dex,
		t,
	});
	const marketDescription = getMarketDescription({
		coin: currentCoin,
		dex,
		displayName: getDefaultPerpetualDisplayName(currentCoin, dex),
		symbolLabel,
	});
	const translatedDescription = t(marketDescription.descriptionKey, {
		name: localizedSubtitle,
	});
	const modalDescription =
		translatedDescription !== marketDescription.descriptionKey
			? translatedDescription
			: marketDescription.fallbackDescription;

	function renderAssetLogo(sizeClassName: string, textClassName: string) {
		if (logo.logoFailed) {
			return (
				<div
					className={`flex shrink-0 items-center justify-center rounded-full bg-trade-interactive text-trade-text-strong ${sizeClassName} ${textClassName}`}
				>
					{symbolBadgeLabel}
				</div>
			);
		}

		return (
			<img
				src={logo.logoSrc}
				alt={getMarketLogoAlt({ coin: currentCoin, dex, t })}
				className={`shrink-0 rounded-full ${sizeClassName}`}
				onError={logo.onLogoError}
			/>
		);
	}

	return (
		<div className="flex h-full min-w-0 items-center gap-0.5">
			<Tooltip>
				<Dialog>
					<TooltipTrigger asChild>
						<DialogTrigger asChild>
							<button
								type="button"
								className="flex shrink-0 cursor-pointer items-center justify-center rounded-full outline-none transition-transform hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-trade-accent/40"
								aria-label={t("market-info.asset-details-open-aria", {
									market: marketDescription.modalTitle,
								})}
							>
								{renderAssetLogo(
									"h-5 w-5 md:h-7 md:w-7",
									"text-label1-bold",
								)}
							</button>
						</DialogTrigger>
					</TooltipTrigger>
					<TooltipContent side="bottom" align="start" showArrow={false}>
						{t("market-info.asset-details-tooltip")}
					</TooltipContent>
					<DialogContent
						className="w-[min(540px,calc(100vw-2rem))] rounded-[24px] border border-trade-border-subtle bg-[linear-gradient(180deg,var(--trade-surface-overlay),var(--trade-surface-overlay-end))] p-5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03),0_36px_72px_rgba(0,0,0,0.45)] backdrop-blur-md md:p-6"
						onCloseAutoFocus={(event) => event.preventDefault()}
					>
						<div className="flex justify-end">
							<DialogCloseButton
								aria-label={t("market-info.asset-details-close-aria")}
							/>
						</div>
						<div className="mt-1 flex flex-col items-center text-center">
							{renderAssetLogo(
								"h-10 w-10 md:h-12 md:w-12",
								"text-title2",
							)}
							<DialogTitle className="mt-4 text-title2 md:text-title1 text-trade-text-strong">
								{marketDescription.modalTitle}
							</DialogTitle>
							<DialogDescription className="mt-4 max-w-[420px] text-left text-label1 md:text-body2 text-trade-text-secondary">
								{modalDescription}
							</DialogDescription>
						</div>
					</DialogContent>
				</Dialog>
			</Tooltip>
			<PerpMarketSearchDialog
				currentCoin={currentCoin}
				trigger={
					<Button
						variant="ghost"
						className="flex h-full min-w-0 items-center gap-2 p-0 text-left hover:bg-transparent"
					>
						<span className="min-w-0">
							<span
								data-slot="market-search-title"
								className="block truncate text-body2-bold md:text-title1 text-trade-text-strong"
							>
								{symbolLabel}
							</span>
							<span
								data-slot="market-search-subtitle"
								className="block truncate text-caption1-medium md:text-label2 text-trade-text-secondary"
							>
								{localizedSubtitle}
							</span>
						</span>
						<ChevronDown
							data-slot="popover-trigger-icon"
							className="h-4 w-4 shrink-0 text-trade-text-secondary"
						/>
					</Button>
				}
			/>
		</div>
	);
}
